import Enums from './enums';

/**
 * <pre>
 * UgcWall config.
 *
 * Summary of responsibilities:
 *
 *  - defines any commonly used config values specific to UgcWall.
 * </pre>
 *
 * @author Tim Loram
 *
 * @memberof module:UgcWall
 * @namespace
 *
 * @type {Object}
 */
const Config = {
  /**
   * The publicly visible name to use when exposing this apps api methods.
   * @default
   */
  publicName: 'UgcWall',

  /**
   * The key to use when creating the root store using combineReducers.
   * @default
   */
  stateKey: 'ugc',

  /**
   * Config for social walls.
   *
   * @type {Object}
   * @property {String} stateKey The key to use for this sub part of the state.
   * @property {Object} api Various api endpoints.
   */
  socialcontentwall: {
    stateKey: 'socialcontentwall',

    // Boolean indicating if we can make use of Content Services.
    allowContServ: true,

    // API endpoints.
    api: {
      // Content service version.
      version: {
        // Version 2
        v2: {
          list: '/api/content/3/filter',

          // This endpoint will be called for migrated v2 accounts, but existing/old campaigns with appversion < 70.
          listLegacy: '/api/content/3/legacy',
        },
      },
    },
  },

  /**
   * Config for entries.
   *
   * @type {Object}
   * @property {String} stateKey The key to use for this sub part of the state.
   * @property {Object} api Various api endpoints.
   */
  entries: {
    stateKey: 'entries',

    // Boolean indicating if we can make use of Content Services.
    allowContServ: true,

    // API endpoints.
    api: {
      // Content service version.
      version: {
        // Version 1 (legacy)
        v1: {
          list: '/display/api/content/entryDisplayItems.json',
        },
        // Version 2
        v2: {
          list: '/api/content/3/filter',

          // This endpoint will be called for migrated v2 accounts, but existing/old campaigns with appversion < 70.
          listLegacy: '/api/content/3/legacy',
        },
      },
    },
  },

  /**
   * Config for social content carousel.
   *
   * @type {Object}
   * @property {String} stateKey The key to use for this sub part of the state.
   * @property {Object} api Various api endpoints.
   */
  socialcontentcarousel: {
    stateKey: 'socialcontentcarousel',

    // Boolean indicating if we can make use of Content Services.
    allowContServ: true,

    // API endpoints.
    api: {
      // Content service version.
      version: {
        // Version 2
        v2: {
          list: '/api/content/3/filter',

          // This endpoint will be called for migrated v2 accounts, but existing/old campaigns with appversion < 70.
          listLegacy: '/api/content/3/legacy',
        },
      },
    },
  },

  /**
   * Config for collage.
   *
   * @type {Object}
   * @property {String} stateKey The key to use for this sub part of the state.
   * @property {Object} api Various api endpoints.
   */
  collage: {
    stateKey: 'collage',

    // Boolean indicating if we can make use of Content Services.
    allowContServ: true,

    // API endpoints.
    api: {
      // Content service version.
      version: {
        // Version 2
        v2: {
          list: '/api/content/3/filter',

          // This endpoint will be called for migrated v2 accounts, but existing/old campaigns with appversion < 70.
          listLegacy: '/api/content/3/legacy',
        },
      },
    },
  },

  /**
   * Config for content voting contest.
   *
   * @type {Object}
   * @property {String} stateKey The key to use for this sub part of the state.
   * @property {Object} api Various api endpoints.
   */
  voting: {
    stateKey: 'voting',

    // Boolean indicating if we can make use of Content Services.
    allowContServ: true,

    // Set default module config values
    enableVoting: true,
    showMoreMode: Enums.SHOW_MORE_MODE_REPLACE,

    // API endpoints.
    api: {
      // Content service version.
      version: {
        // Version 2
        v2: {
          list: '/api/content/3/filter',

          // This endpoint will be called for migrated v2 accounts, but existing/old campaigns with appversion < 70.
          listLegacy: '/api/content/3/legacy',
        },
      },
    },
  },

  /**
   * Config for filtering.
   *
   * @type {Object}
   * @property {Integer} userInputBuffer Time in ms to buffer user text input before applying a filter (eg for username search).
   */
  filtering: {
    userInputBuffer: 750,
  },
};

export default Config;
